import { BadgeCheck, ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  eligibilityCarrierIcons,
  eligibilityCarrierLabels,
  type EligibilityFilterOption,
  eligibilityFilterOptions,
} from '@/models/salesLead'

function OptionLabel({ option }: { option: EligibilityFilterOption }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <img src={eligibilityCarrierIcons[option.carrier]} alt="" className="size-4 shrink-0 rounded-sm" />
      <span className="truncate">
        {eligibilityCarrierLabels[option.carrier]} {option.eligible ? 'eligible' : 'not eligible'}
      </span>
    </span>
  )
}

interface EligibilitySelectProps {
  // Comma-separated option values, as the `eligibility` URL param holds them; '' for none.
  value: string
  onChange: (value: string) => void
  className?: string
}

export function EligibilitySelect({ value, onChange, className }: EligibilitySelectProps) {
  const picked = new Set(value.split(','))
  const selected = eligibilityFilterOptions.filter((option) => picked.has(option.value))

  function toggle(optionValue: string) {
    const next = new Set(selected.map((option) => option.value))
    if (next.has(optionValue)) next.delete(optionValue)
    else next.add(optionValue)
    onChange(
      eligibilityFilterOptions
        .filter((option) => next.has(option.value))
        .map((option) => option.value)
        .join(',')
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outlineThin" className={cn('justify-start gap-2 font-normal', className)}>
          <BadgeCheck className="text-muted-foreground size-4 shrink-0" />
          <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">Any eligibility</span>
            ) : (
              <>
                <OptionLabel option={selected[0]} />
                {selected.length > 1 && (
                  <span className="text-muted-foreground shrink-0 text-xs">+{selected.length - 1}</span>
                )}
              </>
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange('')}
          disabled={selected.length === 0}
          className="text-muted-foreground w-full justify-start font-normal"
        >
          Any eligibility
        </Button>
        <div className="bg-border -mx-1 my-1 h-px" />
        {eligibilityFilterOptions.map((option) => (
          <label
            key={option.value}
            className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
          >
            <Checkbox checked={picked.has(option.value)} onCheckedChange={() => toggle(option.value)} />
            <OptionLabel option={option} />
          </label>
        ))}
      </PopoverContent>
    </Popover>
  )
}
